import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

import { capitalizeFirstLetter } from '../utils/dataHelpers';
import {
  svgText,
  drawLines,
  drawShapes,
  getLinearScale,
  getPointScale,
  getBandScale,
  xPositions,
  getSeasons,
  getIndicatorValueForScale,
  getIndicatorValueForDisplay,
  getTooltipTitle,
  circleSizeConfig,
} from '../utils/chartsHelpers';
import getSeasonTooltip from '../utils/seasonsHelpers';
import HtmlTooltip from './HtmlTooltip';
import TooltipContent from './TooltipContent';

const singleSeasonKey = 'singleAxis';
const greyColor = '#d9d9d9';
const seasonTextPxSizePrint = 14; // Season text size for print mode

/**
 * Finds reference value for a given season in data
 * @param {string} season - Searched season
 * @param {Array} data - Data objects array
 * @returns {number} Reference value or undefined
 */
export const getRefBySeason = (season, data = []) =>
  data.find(({ label, type_ind: type }) => label === season || type === season)?.ref;

/**
 * Builds line data for circle connections
 * @param {Array} indicatorData - Indicator data
 * @param {Array} seasonFormatedData - Formatted seasonal data
 * @param {Object} configChartsPoint - Chart points configuration
 * @returns {Array} Line data to draw
 */
export const getLinesDrawingData = (indicatorData = [], seasonFormatedData = [], configChartsPoint = {}) => {
  if (!Array.isArray(seasonFormatedData) || !xPositions || !xPositions[0]) {
    return [];
  }

  return seasonFormatedData
    .filter(({ type }) => type !== xPositions[0])
    .map(({ x, value, type, season, axisSeason }) => {
      const refPointValue = getRefBySeason(season, indicatorData);
      return ({
        xStart: 'ref',
        yStart: refPointValue,
        xEnd: x,
        yEnd: value,
        color: configChartsPoint[type]?.color,
        lineWidth: configChartsPoint[type]?.lineWidth,
        season,
        axisSeason,
      });
    });
};

/**
 * Formats data for chart display
 * Adds axisSeason attribute which designates on which x season scale to draw
 *
 * @param {boolean} isSingleSeasonRow - Single line display
 * @param {Array} data - Source data
 * @returns {Array} Formatted data with seasonal structure
 */
export const formatData = (isSingleSeasonRow, data = []) =>
  data.map(({ label, ref, min, mean, max, type_ind: typeInd }) => {
    const season = label || typeInd;
    const axisSeason = isSingleSeasonRow ? singleSeasonKey : season;

    return ({
      seasonLabel: season,
      seasonData: [
        { x: 'ref', type: 'ref', value: ref, season, axisSeason },
        { x: 'target', type: 'min', value: min, season, axisSeason },
        { x: 'target', type: 'max', value: max, season, axisSeason },
        { x: 'target', type: 'mean', value: mean, season, axisSeason },
      ].filter(({ value }) => value !== null),
    });
  });

/**
 * Chart - Climate chart component with D3.js and SVG
 * Handles display of climate data with scales, shapes and tooltips
 *
 * @param {Object} props - Component props
 * @param {string} [props.indicatorKey] - Indicator key
 * @param {Object} [props.configChartsPoint] - Chart points configuration
 * @param {number} [props.width] - Chart width
 * @param {number} [props.height] - Chart height
 * @param {Array} [props.data] - Data to display
 * @param {string} [props.selectedSeason] - Selected season
 * @param {boolean} [props.isSingleSeasonRow] - Single line display
 * @param {string} [props.title] - Chart title
 * @param {boolean} [props.isReadingGuide] - Sample chart of the reading guide
 * @param {boolean} [props.isPrint] - Print mode
 * @param {string} [props.screenType] - Screen type
 * @param {string} [props.domain] - Territory domain for season tooltips
 * @returns {React.ReactElement} SVG chart with climate data
 */
const Chart = ({
  indicatorKey = 'G1',
  configChartsPoint = {},
  width = 250,
  height = 350,
  data = [],
  selectedSeason = '',
  isSingleSeasonRow = false,
  title,
  isReadingGuide = false,
  isPrint = false,
  screenType = 'largeScreen',
  domain,
}) => {
  const indicatorScaleValue = getIndicatorValueForScale(indicatorKey);
  const indicatorDisplayValue = getIndicatorValueForDisplay(indicatorKey);
  const indicatorTooltipTitle = getTooltipTitle(indicatorKey);

  // Data formatting: apply scale transformation for Y-axis calculations
  const formatedData = useMemo(() =>
    data.map(({ label, ref, min, mean, max, type_ind }) => ({
      label,
      type_ind,
      ref: indicatorScaleValue(ref),
      min: indicatorScaleValue(min),
      mean: indicatorScaleValue(mean),
      max: indicatorScaleValue(max),
    })),
    [data, indicatorScaleValue],
  );

  const flatedData = useMemo(
    () => formatData(isSingleSeasonRow, formatedData),
    [isSingleSeasonRow, formatedData],
  );

  // Y scales calculation
  const ySerie = flatedData.flatMap(({ seasonData }) => seasonData).map(({ value }) => value);
  const yMin = Math.min(...ySerie);
  const yMax = Math.max(...ySerie);
  let hMin = yMin;
  let hMax = yMax;

  const seasons = isSingleSeasonRow ? [singleSeasonKey] : getSeasons(data);

  // Season display management
  let displaySeasons = seasons
    .filter(season => season !== 'annuel')
    .map(season => ({
      position: season,
      label: capitalizeFirstLetter(season),
    }));

  if (isSingleSeasonRow) {
    displaySeasons = selectedSeason ? [capitalizeFirstLetter(selectedSeason)] : [];
  }

  // Margins and sizes configuration
  const isAnnualIndicator = !isSingleSeasonRow && seasons.length === 1;
  const circleTextMarginRatio = (isAnnualIndicator || isSingleSeasonRow) ? 1.7 : 1.4;
  const circleSize = (isPrint && isAnnualIndicator)
    ? circleSizeConfig.season
    : circleSizeConfig.annual;
  const yPxMargin = circleSize * circleTextMarginRatio;
  const seasonTextMarginPx = isPrint ? seasonTextPxSizePrint : 25;
  const seasonTextMargin = (!isSingleSeasonRow && displaySeasons.length > 1)
    ? seasonTextMarginPx
    : 0;
  const yMarginTop = yPxMargin + seasonTextMargin;
  const yMarginBottom = yPxMargin;

  // Scale adaptation for annual indicator
  if (isAnnualIndicator) {
    const average = ySerie.reduce((a, b) => a + b, 0) / ySerie.length;
    hMin = Math.min(Math.min(...ySerie), 0.95 * average);
    hMax = Math.max(Math.max(...ySerie), 1.05 * average);
  }

  // Dedicated Y scales for single season display
  const yScalesBySeasons = {};
  if (isSingleSeasonRow && selectedSeason) {
    const seasonsRanges = formatedData.map(({ ref, min, max }) =>
      Math.max(ref, min, max) - Math.min(ref, min, max));
    const dyMax = Math.max(...seasonsRanges);

    formatedData.forEach(({ label, ref, min, max }) => {
      const minVal = Math.min(ref, min, max);
      const maxVal = Math.max(ref, min, max);
      const yRange = [
        (minVal + maxVal) / 2 - 0.5 * dyMax,
        (minVal + maxVal) / 2 + 0.5 * dyMax,
      ];

      yScalesBySeasons[label] = getLinearScale(
        [height - yMarginBottom, yMarginTop],
        [yRange[0], yRange[1]],
      );
    });
  }

  // Scales configuration
  const paddingInner = 0.4;
  let paddingOuter = 0.1;
  if (isPrint || screenType === 'mediumScreen') { paddingOuter = 0.2; }
  const xSeason = getBandScale([0, width], seasons, paddingInner, paddingOuter);
  const yScale = getLinearScale([height - yMarginBottom, yMarginTop], [hMin, hMax]);
  const xPoint = getPointScale([0, xSeason.bandwidth()], xPositions);
  const xScale = (x, season) => xSeason(season) + xPoint(x);

  // Drawing data generation for each season
  const drawingData = useMemo(() =>
    flatedData.map(({ seasonLabel, seasonData }) => {
      const seasonYscale = !isSingleSeasonRow
        ? yScale
        : yScalesBySeasons[seasonLabel] || yScale;

      const seasonLines = getLinesDrawingData(formatedData, seasonData, configChartsPoint) || [];
      const linesDisplay = drawLines(
        xScale, seasonYscale, indicatorKey, seasonLines, greyColor,
      );
      const shapesDisplay = drawShapes(
        xScale,
        seasonYscale,
        configChartsPoint,
        indicatorKey,
        seasonData,
        greyColor,
        indicatorDisplayValue,
        isPrint,
      );

      return ({
        seasonLabel,
        linesDisplay,
        shapesDisplay,
      });
    }),
    [flatedData, isSingleSeasonRow, yScale, yScalesBySeasons, formatedData, configChartsPoint, xScale, indicatorKey, indicatorDisplayValue, isPrint],
  );

  // Get tooltip for single season display (outside SVG)
  const singleSeasonTooltip = isSingleSeasonRow && displaySeasons && displaySeasons[0]
    ? getSeasonTooltip(displaySeasons[0], domain)
    : null;

  // The reading guide renders a sample chart: naming it after a real indicator
  // would wrongly suggest it holds the entity's data.
  let chartLabel = 'Graphique d\'exemple illustrant la lecture des infographies';
  if (!isReadingGuide) {
    chartLabel = title ? `Graphique : ${title}` : `Graphique : ${indicatorKey}`;
  }

  return (
    <Box className="chart" sx={{ mt: isPrint ? 0 : 1, mb: isPrint ? 0 : 2 }}>
      {isSingleSeasonRow && displaySeasons && (
        singleSeasonTooltip && !isPrint ? (
          <Tooltip title={singleSeasonTooltip} arrow placement="top">
            <Typography
              variant="body1"
              align="center"
              sx={{ fontWeight: 'bold', fontSize: 'h6.fontSize', cursor: 'help' }}
            >
              {displaySeasons}
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            variant="body1"
            align="center"
            sx={{ fontWeight: 'bold', fontSize: 'h6.fontSize' }}
          >
            {displaySeasons}
          </Typography>
        )
      )}
      {/* role="img" + a name: without it the chart exposes its raw <text> values
          as a bare list of numbers ("58, 26, 38, 35"), which conveys nothing.
          A textual equivalent of the data itself is still to be provided. */}
      <svg
        height={`${height}px`}
        width={`${width}px`}
        style={{ overflow: 'visible' }}
        role="img"
        aria-label={chartLabel}
      >
        {drawingData.map(({ seasonLabel, linesDisplay, shapesDisplay }) => {
          const shouldNotDisplaySeason = isSingleSeasonRow && (seasonLabel !== 'annuel') && (seasonLabel !== selectedSeason);
          const dataValues = formatedData.find(({ label, type_ind: typeInd }) =>
            (!shouldNotDisplaySeason && label === seasonLabel) || typeInd === 'annuel'
          );

          const tooltipContent = (
            <TooltipContent
              title={title}
              indicatorKey={indicatorKey}
              indicatorValueForDisplay={indicatorDisplayValue}
              indicatorTooltipTitle={indicatorTooltipTitle}
              seasonLabel={seasonLabel}
              reference={dataValues?.ref}
              min={dataValues?.min}
              mean={dataValues?.mean}
              max={dataValues?.max}
            />
          );

          return !shouldNotDisplaySeason && (
            <HtmlTooltip title={tooltipContent} key={`tooltip-${indicatorKey}-${seasonLabel}`}>
              <g key={`group-${indicatorKey}-${seasonLabel}`}>
                {linesDisplay}
                {shapesDisplay}
              </g>
            </HtmlTooltip>
          );
        })}
        {!isSingleSeasonRow && displaySeasons.map(({ position, label }) => {
          const tooltipText = getSeasonTooltip(label, domain);

          const seasonLabel = svgText({
            x: 'label',
            y: 10,
            text: label,
            season: position,
            xScale,
            yScale: x => x,
            textSize: isPrint ? seasonTextPxSizePrint : 17,
            fill: '#003661',
            maxWidth: xSeason.bandwidth() + 40,
          });

          // Wrap season label with Tooltip if tooltip text exists
          if (tooltipText && !isPrint) {
            return (
              <Tooltip
                key={`season-${position}`}
                title={tooltipText}
                arrow
                placement="top"
              >
                <g style={{ cursor: 'help' }}>
                  {seasonLabel}
                </g>
              </Tooltip>
            );
          }

          return seasonLabel;
        })}
      </svg>
    </Box>
  );
};

Chart.displayName = 'Chart';

export default Chart;
